<template>
  <div
    :class="isMini ? 'msgs-chat__mini_body' : 'msgs-chat__body'"
    class="overscroll-contain"
    ref="chatBody"
    @scroll="handleScroll"
  >
    <div v-if="loadingMore" class="msgs-chat__loading-more">
      <font-awesome-icon icon="fa-solid fa-arrows-rotate" class="fa-spin text-sm text-gray-400" />
    </div>

    <div v-if="loading" class="msgs-chat__loading">
      <LoadingSpinner />
    </div>

    <template v-else-if="displayMessages.length > 0">
      <div v-for="(group, dateKey) in groupedMessages" :key="dateKey">
        <div class="msgs-date-sep">
          <span class="msgs-date-sep__label">{{ dateKey }}</span>
        </div>
        
        <TransitionGroup name="msg-list" tag="div" class="space-y-3">
          <MessageChatBubble
            v-for="msg in group"
            :key="msg.id || msg.tempId"
            :msg="msg"
            :is-sent-by-me="isSentByMe(msg)"
            :is-system-convo="isSystemConvo"
            :is-dismissed="isDismissed(msg.id)"
            :unfocused="unfocused"
            :status="msg.status || 'sent'"
            @retry="$emit('retry-send', $event)"
          />
        </TransitionGroup>
      </div>

      <Transition name="msg-list">
        <MessageChatBubble v-if="partnerIsTyping" :is-typing="true" class="mt-2" />
      </Transition>
    </template>

    <div v-else class="msgs-chat__empty">
      <font-awesome-icon icon="fa-solid fa-comments" class="text-4xl text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-sm text-gray-400">
        {{ isSystemConvo ? 'No announcements yet' : 'Start the conversation' }}
      </p>
    </div>
  </div>
</template>

<script>
import MessageChatBubble from './MessageChatBubble.vue'
import LoadingSpinner from "@/components/Loading/LoadingSpinner/LoadingSpinner.vue"

export default {
  name: 'MessageChatBody',
  components: {
    MessageChatBubble,
    LoadingSpinner
  },
  props: {
    displayMessages: {
      type: Array,
      required: true
    },
    isSystemConvo: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    hasMoreMessages: {
      type: Boolean,
      default: true
    },
    partnerIsTyping: {
      type: Boolean,
      default: false
    },
    currentUserId: {
      type: [String, Number],
      required: true
    },
    dismissedIds: {
      type: Array,
      default: () => []
    },
    isMini: {
      type: Boolean,
      default: false
    },
    unfocused: {
      type: Boolean,
      default: false
    }
  },
  emits: ['load-more', 'retry-send'],
  data() {
    return {
      prevScrollHeight: 0,
      localScrollLock: false
    }
  },
  computed: {
    groupedMessages() {
      const groups = {}
      for (const msg of this.displayMessages) {
        const dateKey = this.formatDateLabel(msg.created_at)
        if (!groups[dateKey]) groups[dateKey] = []
        groups[dateKey].push(msg)
      }
      return groups
    }
  },
  watch: {
    loadingMore(val) {
      if (val) {
        const body = this.$refs.chatBody
        this.prevScrollHeight = body ? body.scrollHeight : 0
        this.localScrollLock = true
      }
    },
    displayMessages: {
      deep: true,
      handler(newMsgs, oldMsgs) {
        // Performance guard: Only snap down if the message count actually increased
        if (newMsgs?.length !== oldMsgs?.length) {
          this.scrollToBottom()
        }
      }
    },
    loading(isSearching) {
      if (!isSearching) {
        this.scrollToBottom()
      }
    },
    partnerIsTyping(val) {
      if (val) this.scrollToBottom()
    }
  },
  mounted() {
    // Force a scroll to the latest messages immediately when the component renders
    this.scrollToBottom()
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatBody
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },

    handleScrollInternal(e) {
      const el = e.target
      // If scrolled to top (scrollTop === 0), emit your existing load-more event
      if (el.scrollTop === 0) {
        this.$emit('load-more')
      }
    },
    isSentByMe(msg) {
      return msg.sender_id === this.currentUserId
    },
    isDismissed(id) {
      return this.dismissedIds?.includes(id)
    },
    formatDateLabel(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const diff = today - msgDate
      if (diff === 0) return 'Today'
      if (diff === 86400000) return 'Yesterday'
      return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
/* Smooth upward slide and fade styling for new messages */
.msg-list-enter-active {
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.msg-list-enter-from {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}

/* Maintain position context gracefully if items change indexing */
.msg-list-move {
  transition: transform 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-fadeIn {
  animation: fadeIn 0.2s ease forwards;
}

/* ── Chat body ───────────────────────────── */
.msgs-chat__body,
.msgs-chat__mini_body {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.msgs-chat__mini_body {
  padding: 1rem;
}
.msgs-chat__body {
  padding: 1.5rem;
}
.msgs-chat__loading,
.msgs-chat__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
}
.msgs-chat__loading-more {
  display: flex;
  justify-content: center;
  padding: 0.5rem;
}

/* ── Date separator ──────────────────────── */
.msgs-date-sep {
  display: flex;
  justify-content: center;
  padding: 0.75rem 0;
}
.msgs-date-sep__label {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: #e5e7eb;
  color: #6b7280;
}
[data-theme="dark"] .msgs-date-sep__label {
  background: rgba(255, 255, 255, 0.06);
  color: #9ca3af;
}
</style>